import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Copy, Download, FilePlus2, Pencil, Trash2, Plus, FileText, Search, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { http, money, downloadBlob } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { UpgradeDialog } from "../components/UpgradeDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function SavedQuotesPage() {
  const navigate = useNavigate();
  const { credits } = useAuth();
  const [quotes, setQuotes] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null); // quote being edited
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const { data } = await http.get("/quotes");
      setQuotes(data);
    } catch {
      setQuotes([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const total = (q) => {
    const sub = (q.lines || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
    const tax = q.tax_rate ? (sub * Number(q.tax_rate)) / 100 : 0;
    return sub + tax;
  };

  const download = async (q, docType) => {
    const isInvoice = docType === "invoice";
    setBusy(q.id + (isInvoice ? "i" : "d"));
    const number = isInvoice
      ? (q.quote_number || "Q-0000").replace(/^Q-/, "INV-")
      : q.quote_number;
    try {
      const res = await http.post(
        "/generate-pdf",
        {
          quote_number: number,
          date: q.date,
          customer_name: q.customer_name,
          customer_address: q.customer_address,
          customer_phone: q.customer_phone,
          job_title: q.job_title,
          lines: (q.lines || []).map((l) => ({ description: l.description, amount: parseFloat(l.amount) || 0 })),
          note: q.note,
          tax_rate: q.tax_rate,
          template: q.template || "basic",
          doc_type: isInvoice ? "invoice" : "quote",
        },
        { responseType: "blob" }
      );
      downloadBlob(res.data, `${isInvoice ? "invoice" : "quote"}-${number}.pdf`);
    } catch {
      toast.error(`Could not build the ${isInvoice ? "invoice" : "PDF"}.`);
    } finally {
      setBusy("");
    }
  };

  const sendDoc = async (q, docType) => {
    const isInvoice = docType === "invoice";
    if (!q.customer_email) {
      toast.error("Add a customer email (tap Edit) before sending.");
      return;
    }
    setBusy(q.id + (isInvoice ? "si" : "sq"));
    try {
      const { data } = await http.post(`/quotes/${q.id}/send`, { doc_type: isInvoice ? "invoice" : "quote" });
      toast.success(`${isInvoice ? "Invoice" : "Quote"} emailed to ${data.to}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the email.");
    } finally {
      setBusy("");
    }
  };

  const copy = async (q) => {
    setBusy(q.id + "c");
    try {
      await http.post(`/quotes/${q.id}/copy`);
      toast.success("Copied to a new number and date.");
      await load();
    } catch {
      toast.error("Could not copy.");
    } finally {
      setBusy("");
    }
  };

  const openEdit = (q) => {
    setEditing(q);
    setForm({
      customer_name: q.customer_name || "",
      customer_address: q.customer_address || "",
      customer_phone: q.customer_phone || "",
      customer_email: q.customer_email || "",
      job_title: q.job_title || "",
      note: q.note || "",
      tax_rate: q.tax_rate ?? "",
      lines: (q.lines || []).map((l) => ({
        id: crypto.randomUUID(),
        description: l.description || "",
        amount: l.amount ?? "",
      })),
    });
  };

  const setLine = (id, k, v) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.id === id ? { ...l, [k]: v } : l)) }));
  const addLine = () =>
    setForm((f) => ({ ...f, lines: [...f.lines, { id: crypto.randomUUID(), description: "", amount: "" }] }));
  const removeLine = (id) => setForm((f) => ({ ...f, lines: f.lines.filter((l) => l.id !== id) }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      await http.put(`/quotes/${editing.id}`, {
        customer_name: form.customer_name || null,
        customer_address: form.customer_address || null,
        customer_phone: form.customer_phone || null,
        customer_email: form.customer_email || null,
        job_title: form.job_title || null,
        note: form.note || null,
        tax_rate: form.tax_rate ? parseFloat(form.tax_rate) : null,
        lines: form.lines.map((l) => ({ description: l.description, amount: parseFloat(l.amount) || 0 })),
      });
      toast.success("Quote updated.");
      setEditing(null);
      await load();
    } catch {
      toast.error("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const q = deleteTarget;
    setBusy(q.id + "x");
    try {
      await http.delete(`/quotes/${q.id}`);
      toast.success("Quote deleted.");
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error("Could not delete.");
    } finally {
      setBusy("");
    }
  };

  const editTotal = form
    ? (() => {
        const sub = form.lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
        const tax = form.tax_rate ? (sub * Number(form.tax_rate)) / 100 : 0;
        return sub + tax;
      })()
    : 0;

  const filtered = (quotes || []).filter((q) => {
    const s = query.trim().toLowerCase();
    if (!s) return true;
    return [q.quote_number, q.customer_name, q.job_title].some((x) => (x || "").toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen">
      <Header onUpgrade={() => setUpgradeOpen(true)} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-4xl font-bold text-[#1C1915]">Saved quotes</h1>
          <Button data-testid="new-quote-button" onClick={() => navigate("/")} className="rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white">
            <FilePlus2 className="w-4 h-4 mr-2" /> New quote
          </Button>
        </div>

        {quotes === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#A6844A]" />
          </div>
        ) : quotes.length === 0 ? (
          <div data-testid="empty-quotes" className="text-center py-20 text-[#5C564E]">
            <p className="font-serif text-xl text-[#1B3A4B]">No saved quotes yet.</p>
            <p className="mt-2">Paid quotes you make are stored here.</p>
          </div>
        ) : (
          <>
            <div className="relative mb-6 max-w-md">
              <Search className="w-4 h-4 text-[#5C564E] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="quote-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by quote #, customer, or job…"
                className="pl-9 rounded-full bg-white/80"
              />
            </div>
            {filtered.length === 0 ? (
              <div data-testid="no-quote-match" className="text-center py-16 text-[#5C564E]">
                No quotes match “{query}”.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((q) => (
              <div key={q.id} data-testid="quote-card" className="bg-white/80 border border-[#E2DCD2] rounded-xl shadow-sm p-5 hover:-translate-y-0.5 transition-transform duration-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#A6844A]">{q.quote_number}</span>
                  <span className="text-xs text-[#5C564E]">{q.date}</span>
                </div>
                <h3 className="font-serif text-lg text-[#1B3A4B] mt-2 line-clamp-2">{q.job_title || "Quote"}</h3>
                <p className="text-sm text-[#5C564E] mt-1">{q.customer_name || "—"}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-[#5C564E]">{(q.lines || []).length} lines</span>
                  <span className="font-serif text-xl font-bold text-[#1C1915]">{money(total(q))}</span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Button data-testid="quote-download" size="sm" onClick={() => download(q, "quote")} disabled={!!busy} className="rounded-full bg-[#A6844A] hover:bg-[#8f6f3b] text-white">
                    {busy === q.id + "d" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> PDF</>}
                  </Button>
                  <Button data-testid="quote-invoice" size="sm" onClick={() => download(q, "invoice")} disabled={!!busy} className="rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white">
                    {busy === q.id + "i" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-3.5 h-3.5 mr-1" /> Invoice</>}
                  </Button>
                  <Button data-testid="quote-send-quote" size="sm" variant="outline" onClick={() => sendDoc(q, "quote")} disabled={!!busy} className="rounded-full border-[#A6844A] text-[#8f6f3b]">
                    {busy === q.id + "sq" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Send Quote</>}
                  </Button>
                  <Button data-testid="quote-send-invoice" size="sm" variant="outline" onClick={() => sendDoc(q, "invoice")} disabled={!!busy} className="rounded-full border-[#1B3A4B] text-[#1B3A4B]">
                    {busy === q.id + "si" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Send Invoice</>}
                  </Button>
                  <Button data-testid="quote-edit" size="sm" variant="outline" onClick={() => openEdit(q)} disabled={!!busy} className="rounded-full border-[#1B3A4B] text-[#1B3A4B]">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button data-testid="quote-copy" size="sm" variant="outline" onClick={() => copy(q)} disabled={!!busy} className="rounded-full border-[#D5CFC2] text-[#1B3A4B]">
                    {busy === q.id + "c" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
                  </Button>
                  <Button data-testid="quote-delete" size="sm" variant="ghost" onClick={() => setDeleteTarget(q)} disabled={!!busy} className="rounded-full text-[#9E2A2B] hover:bg-[#9E2A2B]/10">
                    {busy === q.id + "x" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</>}
                  </Button>
                </div>
              </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-quote-dialog" className="bg-[#F8F6F1] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1B3A4B]">
              Edit {editing?.quote_number}
            </DialogTitle>
            <DialogDescription className="text-[#5C564E]">
              Update the line items and details, then save or download it as an invoice.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Job title</Label>
                <Input data-testid="edit-job-title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Customer</Label>
                  <Input data-testid="edit-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Phone</Label>
                  <Input data-testid="edit-customer-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Address</Label>
                <Input data-testid="edit-customer-address" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Customer email</Label>
                <Input data-testid="edit-customer-email" type="email" placeholder="maria@example.com" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Lines</Label>
                <div className="space-y-2 mt-1.5">
                  {form.lines.map((l) => (
                    <div key={l.id} className="flex items-center gap-2">
                      <Input data-testid="edit-line-description" className="flex-1" placeholder="description" value={l.description} onChange={(e) => setLine(l.id, "description", e.target.value)} />
                      <Input data-testid="edit-line-amount" type="number" className="w-24 font-mono" placeholder="0.00" value={l.amount} onChange={(e) => setLine(l.id, "amount", e.target.value)} />
                      <Button variant="ghost" size="icon" onClick={() => removeLine(l.id)} className="text-[#9E2A2B] hover:bg-[#9E2A2B]/10 shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button data-testid="edit-add-line" variant="outline" size="sm" onClick={addLine} className="mt-2 rounded-full border-[#A6844A] text-[#1B3A4B]">
                  <Plus className="w-4 h-4 mr-1" /> Add a line
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Tax rate %</Label>
                  <Input data-testid="edit-tax-rate" type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
                </div>
                <div className="flex items-end justify-end">
                  <span className="font-serif text-xl font-bold text-[#1C1915]">Total {money(editTotal)}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Note</Label>
                <Textarea data-testid="edit-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="edit-save" onClick={saveEdit} disabled={saving} className="rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-quote-dialog" className="bg-[#F8F6F1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#1B3A4B]">Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C564E]">
              {deleteTarget?.quote_number} will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={confirmDelete} className="rounded-full bg-[#9E2A2B] hover:bg-[#832223] text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      <Footer />
    </div>
  );
}
